import { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Field } from "./Orders";
import KpiCard from "../components/KpiCard";
import { formatCurrency, formatDateTime } from "../lib/format";
import {
    Plus,
    TrashSimple,
    ArrowUpRight,
    ArrowDownRight,
    Wallet,
    Notebook,
    Coins,
} from "@phosphor-icons/react";

export default function Khatabook() {
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [filterType, setFilterType] = useState("");
    const [filterParty, setFilterParty] = useState("");

    const [form, setForm] = useState({
        party_name: "",
        party_mobile: "",
        entry_type: "credit",
        method: "cash",
        amount: 0,
        note: "",
    });

    const load = async () => {
        setLoading(true);
        const params = {};
        if (filterType) params.entry_type = filterType;
        if (filterParty) params.party = filterParty;
        const [p, s] = await Promise.all([
            api.get("/payments", { params }),
            api.get("/payments/summary"),
        ]);
        setPayments(p.data);
        setSummary(s.data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filterType, filterParty]);

    const onSubmit = async (e) => {
        e.preventDefault();
        await api.post("/payments", { ...form, amount: Number(form.amount) || 0 });
        setShowModal(false);
        setForm({
            party_name: "",
            party_mobile: "",
            entry_type: "credit",
            method: "cash",
            amount: 0,
            note: "",
        });
        load();
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this entry?")) return;
        await api.delete(`/payments/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="khatabook-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="label-uppercase">Ledger</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                        Khatabook
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Digital double-entry book — credits, debits, and party balances.
                    </p>
                </div>
                <button
                    data-testid="new-entry-btn"
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-xl font-medium btn-glow"
                >
                    <Plus weight="bold" size={16} /> New Entry
                </button>
            </div>

            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <KpiCard
                        label="Total Credit"
                        value={formatCurrency(summary.credit)}
                        sub={`${summary.credit_count} entries`}
                        icon={ArrowDownRight}
                        accent="from-emerald-500/30 to-emerald-500/0"
                        testId="kpi-credit"
                    />
                    <KpiCard
                        label="Total Debit"
                        value={formatCurrency(summary.debit)}
                        sub={`${summary.debit_count} entries`}
                        icon={ArrowUpRight}
                        accent="from-rose-500/30 to-rose-500/0"
                        testId="kpi-debit"
                    />
                    <KpiCard
                        label="Net Balance"
                        value={formatCurrency(summary.net)}
                        icon={Wallet}
                        accent="from-blue-500/30 to-blue-500/0"
                        testId="kpi-net"
                    />
                    <KpiCard
                        label="Parties"
                        value={(summary.parties || []).length}
                        sub="Active accounts"
                        icon={Notebook}
                        accent="from-violet-500/30 to-violet-500/0"
                        testId="kpi-parties"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 glass-card">
                    <div className="p-4 flex flex-wrap gap-3 items-center border-b border-white/5">
                        <input
                            placeholder="Filter by party…"
                            value={filterParty}
                            onChange={(e) => setFilterParty(e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px] focus:outline-none focus:border-blue-500/60"
                            data-testid="filter-party"
                        />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60"
                            data-testid="filter-type"
                        >
                            <option value="">All types</option>
                            <option value="credit">Credit (Received)</option>
                            <option value="debit">Debit (Paid)</option>
                        </select>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="payments-table">
                            <thead>
                                <tr className="text-left text-slate-400 border-b border-white/5">
                                    <th className="px-5 py-3 label-uppercase">Date</th>
                                    <th className="px-5 py-3 label-uppercase">Party</th>
                                    <th className="px-5 py-3 label-uppercase">Method</th>
                                    <th className="px-5 py-3 label-uppercase text-right">Amount</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => (
                                    <tr
                                        key={p.id}
                                        className="border-b border-white/5 hover:bg-white/[0.03]"
                                    >
                                        <td className="px-5 py-3 text-slate-300 text-xs">
                                            {formatDateTime(p.date)}
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="font-medium">{p.party_name}</div>
                                            {p.note && (
                                                <div className="text-xs text-slate-500">
                                                    {p.note}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span className="text-xs uppercase tracking-widest text-slate-400">
                                                {p.method}
                                            </span>
                                        </td>
                                        <td
                                            className={`px-5 py-3 text-right font-display font-bold ${
                                                p.entry_type === "credit"
                                                    ? "text-emerald-300"
                                                    : "text-rose-300"
                                            }`}
                                        >
                                            {p.entry_type === "credit" ? "+" : "−"}
                                            {formatCurrency(p.amount)}
                                        </td>
                                        <td className="px-5 py-3">
                                            <button
                                                onClick={() => onDelete(p.id)}
                                                className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400"
                                            >
                                                <TrashSimple size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && payments.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                                            <Coins size={28} className="mx-auto mb-2 opacity-50" />
                                            No entries yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="glass-card p-5">
                    <div className="label-uppercase">Party Ledger</div>
                    <div className="font-display text-lg font-semibold mt-1">Balances</div>
                    <div className="mt-4 space-y-2 max-h-[440px] overflow-auto pr-1">
                        {(summary?.parties || []).map((p) => (
                            <div
                                key={p.party}
                                className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5"
                            >
                                <div className="min-w-0">
                                    <div className="font-medium text-sm truncate">{p.party}</div>
                                    <div className="text-xs text-slate-500">
                                        Cr: {formatCurrency(p.credit)} · Dr:{" "}
                                        {formatCurrency(p.debit)}
                                    </div>
                                </div>
                                <div
                                    className={`font-display font-bold text-sm ${
                                        p.balance >= 0
                                            ? "text-emerald-300"
                                            : "text-rose-300"
                                    }`}
                                >
                                    {formatCurrency(p.balance)}
                                </div>
                            </div>
                        ))}
                        {(summary?.parties || []).length === 0 && (
                            <div className="text-sm text-slate-500 py-6 text-center">
                                No parties yet.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <Modal title="New Khatabook Entry" onClose={() => setShowModal(false)}>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Party Name" required>
                                <input
                                    required
                                    className="input"
                                    value={form.party_name}
                                    onChange={(e) =>
                                        setForm({ ...form, party_name: e.target.value })
                                    }
                                    data-testid="entry-party"
                                />
                            </Field>
                            <Field label="Mobile">
                                <input
                                    className="input"
                                    value={form.party_mobile}
                                    onChange={(e) =>
                                        setForm({ ...form, party_mobile: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Type" required>
                                <select
                                    className="input"
                                    value={form.entry_type}
                                    onChange={(e) =>
                                        setForm({ ...form, entry_type: e.target.value })
                                    }
                                    data-testid="entry-type"
                                >
                                    <option value="credit">Credit (Received)</option>
                                    <option value="debit">Debit (Paid / Expense)</option>
                                </select>
                            </Field>
                            <Field label="Method">
                                <select
                                    className="input"
                                    value={form.method}
                                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                                >
                                    <option value="cash">Cash</option>
                                    <option value="upi">UPI</option>
                                    <option value="bank">Bank</option>
                                    <option value="card">Card</option>
                                    <option value="other">Other</option>
                                </select>
                            </Field>
                            <Field label="Amount (₹)" required>
                                <input
                                    type="number"
                                    required
                                    className="input"
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    data-testid="entry-amount"
                                />
                            </Field>
                        </div>
                        <Field label="Note">
                            <textarea
                                rows={2}
                                className="input"
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                            />
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="save-entry"
                                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow font-medium text-sm"
                            >
                                Save Entry
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
